import { useAuth } from "@/src/contexts/auth.context";
import { deleteEntityFiles } from "@/src/services/api/storage/storage.service";
import {
  DeleteEventGalleryResponse,
  DeleteEventResponse,
  DeleteRSVPResponse,
  GetEventGalleryResponse,
  GetEventsInput,
  GetEventsResponse,
  GetRSVPsResponse,
  InsertEventGalleryResponse,
  InsertEventInput,
  InsertEventResponse,
  InsertRSVPResponse,
  RSVPParticipantSnapshot,
  RSVPStatus,
  UpdateEventResponse,
  UpdateRSVPResponse,
} from "@/src/models/event.model";
import {
  EVENT_GALLERY_QUERY_KEY,
  EVENTS_QUERY_KEY,
  RSVPS_QUERY_KEY,
} from "@/src/query-keys/events.keys";
import gqlClient from "@/src/services/api/graphql.client";
import { sendNotification } from "@/src/services/api/notification/notification.service";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { router } from "expo-router";
import { useTranslation } from "react-i18next";
import Toast from "react-native-toast-message";
import {
  DELETE_EVENT,
  DELETE_EVENT_GALLERY,
  DELETE_RSVP,
  GET_EVENT_GALLERY,
  GET_EVENTS,
  GET_RSVPS,
  INSERT_EVENT,
  INSERT_EVENT_GALLERY,
  INSERT_RSVP,
  UPDATE_EVENT,
  UPDATE_RSVP,
} from "./events.query";

const useInvalidateEvents = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [EVENTS_QUERY_KEY] });
};

export const useCreateEventMutation = () => {
  const invalidate = useInvalidateEvents();
  const { user } = useAuth();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (input: InsertEventInput) => {
      const client = await gqlClient();
      const data = await client.request<InsertEventResponse>(INSERT_EVENT, {
        ...input,
        firstName: user?.firstName ?? "",
        lastName: user?.lastName ?? "",
        email: user?.email ?? "",
        profileImageUrl: user?.profileImageUrl ?? "",
      });
      return data.insertevent;
    },
    onSuccess: (data) => {
      invalidate();

      // Broadcast to all users — use roles instead
      sendNotification({
        userIds: [""],
        denormalizedPayload: JSON.stringify({
          type: "event_created",
          message: t("NOTIFY_EVENT_CREATED", {
            firstName: user?.firstName,
            lastName: user?.lastName,
          }),
          actorAvatar: user?.profileImageUrl,
          actorName: `${user?.firstName} ${user?.lastName}`,
          actorId: user?.itemId,
          eventId: data.itemId,
          targetEntityId: data.itemId,
        }),
        configurationName: "event-created",
        responseKey: "event-action",
        responseValue: "event_created",
      }).catch((e) => console.error("Event notification failed:", e));

      router.push("/events");
      Toast.show({ type: "success", text1: "SUCCESS_EVENT_CREATED" });
    },
    onError: () => {
      Toast.show({ type: "error", text1: "ERROR_EVENT_CREATION_FAILED" });
    },
  });
};

export const useGetCalendarEventsQuery = (input: GetEventsInput) => {
  return useQuery({
    queryKey: [
      EVENTS_QUERY_KEY,
      "calendar",
      input.where,
      input.order,
      input.pageNo,
      input.pageSize,
    ],
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request<GetEventsResponse>(GET_EVENTS, {
        where: input.where,
        order: input.order,
        pageNo: input.pageNo,
        pageSize: input.pageSize,
      });
      return data.getevents;
    },
  });
};

export const useGetEventsQuery = (input: Omit<GetEventsInput, "pageNo">) => {
  return useInfiniteQuery({
    queryKey: [EVENTS_QUERY_KEY, input.where, input.order, input.pageSize],
    queryFn: async ({ pageParam = 1 }) => {
      const client = await gqlClient();
      const data = await client.request<GetEventsResponse>(GET_EVENTS, {
        ...input,
        pageNo: pageParam,
      });
      return data.getevents;
    },
    getNextPageParam: (
      lastPage: GetEventsResponse["getevents"],
      pages: GetEventsResponse["getevents"][],
    ) => (lastPage.hasNextPage ? pages.length + 1 : undefined),
    initialPageParam: 1,
    // Without this it fell back to the global 1-minute default
    // (src/config/react-query.ts), refetching far more often than the
    // comparable posts (2 min) and news (5 min) lists — events change less
    // often intraday than a social feed, so align with news.
    staleTime: 5 * 60 * 1000,
  });
};

export const useGetEventByIdQuery = (id: string) => {
  return useQuery({
    // Must sit under EVENTS_QUERY_KEY so useInvalidateEvents() (fired by every
    // event create/update/delete) refetches the detail page too — otherwise an
    // edit leaves the detail screen serving a stale event (old registration
    // policy, join button, counts) until the cache GCs.
    queryKey: [EVENTS_QUERY_KEY, "detail", id],
    queryFn: async () => {
      const client = await gqlClient();
      const result = await client.request(GET_EVENTS, {
        where: { ItemId: { eq: `${id}` } },
        order: [],
      });
      return result.getevents.items[0] ?? null;
    },
    enabled: !!id && id !== "creation",
  });
};

export const useUpdateEventMutation = () => {
  const invalidate = useInvalidateEvents();
  return useMutation({
    mutationFn: async ({
      input,
      itemId,
    }: {
      input: InsertEventInput;
      itemId: string;
    }) => {
      const client = await gqlClient();
      // FL-013 fix: was data.insertevent (wrong); updateevent is the correct response key
      const data = await client.request<UpdateEventResponse>(UPDATE_EVENT, {
        itemId,
        ...input,
      });
      return data.updateevent;
    },
    onSuccess: () => {
      invalidate();
      router.push("/events");
      Toast.show({ type: "success", text1: "SUCCESS_EVENT_UPDATED" });
    },
    onError: () => {
      Toast.show({ type: "error", text1: "ERROR_EVENT_UPDATE_FAILED" });
    },
  });
};

export const useDeleteEventMutation = () => {
  const invalidate = useInvalidateEvents();
  return useMutation({
/**
     * Accepts either a bare id (existing call sites) or `{itemId, entity}`. When
     * the entity is supplied, its uploaded attachments are deleted from Blocks
     * Storage too — otherwise they are orphaned there forever.
     */
    mutationFn: async (input: string | { itemId: string; entity?: unknown }) => {
      const { itemId, entity } =
        typeof input === "string" ? { itemId: input, entity: undefined } : input;
      const client = await gqlClient();
      const data = await client.request<DeleteEventResponse>(DELETE_EVENT, {
        itemId,
      });
      await deleteEntityFiles(entity);
      return data.deleteevent;
    },
    onSuccess: () => {
      invalidate();
      Toast.show({ type: "success", text1: "SUCCESS_EVENT_DELETED" });
    },
    onError: () => {
      Toast.show({ type: "error", text1: "ERROR_EVENT_DELETE_FAILED" });
    },
  });
};

// RSVP

const useInvalidateRSVPs = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [RSVPS_QUERY_KEY] });
};

export const useGetUserRSVPQuery = (
  userId: string,
  eventId: string,
  options?: { enabled?: boolean },
) => {
  const where = { CreatedBy: { eq: userId }, eventId: { eq: eventId } };

  return useQuery({
    queryKey: [RSVPS_QUERY_KEY, "user", userId, eventId],
    enabled: !!userId && !!eventId && (options?.enabled ?? true),
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request<GetRSVPsResponse>(GET_RSVPS, {
        where,
        order: [],
        pageNo: 1,
        pageSize: 1,
      });
      return data.getRSVPs.items[0] ?? null;
    },
  });
};

export const useGetAttendeeCountQuery = (
  eventId: string,
  options?: { enabled?: boolean },
) => {
  const where = { eventId: { eq: eventId }, status: { eq: "Going" } };

  return useQuery({
    queryKey: [RSVPS_QUERY_KEY, "counts", eventId],
    enabled: eventId.length > 0 && (options?.enabled ?? true),
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request<GetRSVPsResponse>(GET_RSVPS, {
        where,
        order: [],
        pageNo: 1,
        pageSize: 1,
      });
      return data.getRSVPs.totalCount;
    },
  });
};

/**
 * Seats taken for an event: attendees **plus their guests**.
 *
 * `useGetAttendeeCountQuery` counts RSVP rows, which is not the same thing once
 * guests exist — an attendee bringing two guests occupies three seats. Capacity
 * checks use this; the plain attendee count is still right for "N people are
 * going".
 *
 * Needs the rows (not just `totalCount`) to sum `guestNames`, so this pages
 * wide deliberately.
 */
export const useGetEventSeatCountQuery = (
  eventId: string,
  options?: { enabled?: boolean },
) => {
  const where = { eventId: { eq: eventId }, status: { eq: "Going" } };

  return useQuery({
    queryKey: [RSVPS_QUERY_KEY, "seats", eventId],
    enabled: eventId.length > 0 && (options?.enabled ?? true),
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request<GetRSVPsResponse>(GET_RSVPS, {
        where,
        order: [],
        pageNo: 1,
        pageSize: 1000,
      });
      const items = data.getRSVPs.items ?? [];
      const attendees = data.getRSVPs.totalCount ?? items.length;
      const guests = items.reduce(
        (total, rsvp) => total + (rsvp.guestNames?.length ?? 0),
        0,
      );
      return { attendees, guests, seats: attendees + guests };
    },
  });
};

export const useGetWaitlistedRSVPQuery = (
  eventId: string,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: [RSVPS_QUERY_KEY, "waitlist", eventId],
    enabled: !!eventId && enabled,
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request<GetRSVPsResponse>(GET_RSVPS, {
        where: { eventId: { eq: eventId }, status: { eq: "Waiting" } },
        order: [{ field: "CreatedDate", direction: "ASC" }],
        pageNo: 1,
        pageSize: 1,
      });
      return data.getRSVPs.items[0] ?? null;
    },
  });
};

export const useGetRSVPsQuery = (eventId: string, status: RSVPStatus) => {
  const where = { eventId: { eq: eventId }, status: { eq: status } };

  return useQuery({
    queryKey: [RSVPS_QUERY_KEY, "list", eventId, status],
    enabled: !!eventId,
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request<GetRSVPsResponse>(GET_RSVPS, {
        where,
        order: [{ field: "CreatedDate", direction: "ASC" }],
        pageNo: 1,
        pageSize: 100,
      });
      return data.getRSVPs ?? null;
    },
  });
};

export const useInsertRSVPMutation = () => {
  const invalidate = useInvalidateRSVPs();
  return useMutation({
    mutationFn: async ({
      eventId,
      status,
      eventParticipantSnapshot,
      guestNames,
    }: {
      eventId: string;
      status: RSVPStatus;
      eventParticipantSnapshot: RSVPParticipantSnapshot;
      guestNames?: string[];
    }) => {
      const client = await gqlClient();
      const data = await client.request<InsertRSVPResponse>(INSERT_RSVP, {
        eventId,
        status,
        guestNames: guestNames ?? [],
        ...eventParticipantSnapshot,
      });
      return data.insertRSVP;
    },
    onSuccess: () => invalidate(),
    onError: () => Toast.show({ type: "error", text1: "ERROR_RSVP_FAILED" }),
  });
};

export const usePromoteFromWaitlistMutation = () => {
  const invalidate = useInvalidateRSVPs();
  return useMutation({
    mutationFn: async ({ itemId }: { itemId: string }) => {
      const client = await gqlClient();
      const data = await client.request<UpdateRSVPResponse>(UPDATE_RSVP, {
        itemId,
        status: "Going",
      });
      return data.updateRSVP;
    },
    onSuccess: () => invalidate(),
    onError: () => Toast.show({ type: "error", text1: "ERROR_RSVP_FAILED" }),
  });
};

export const useDeleteRSVPMutation = () => {
  const invalidate = useInvalidateRSVPs();
  return useMutation({
    mutationFn: async ({ itemId }: { itemId: string }) => {
      const client = await gqlClient();
      const data = await client.request<DeleteRSVPResponse>(DELETE_RSVP, {
        itemId,
      });
      return data.deleteRSVP;
    },
    onSuccess: () => invalidate(),
    onError: () => Toast.show({ type: "error", text1: "ERROR_RSVP_FAILED" }),
  });
};

export const useUpdateRSVPStatusMutation = () => {
  const invalidate = useInvalidateRSVPs();
  return useMutation({
    mutationFn: async ({ itemId, status }: { itemId: string; status: RSVPStatus }) => {
      const client = await gqlClient();
      const data = await client.request<UpdateRSVPResponse>(UPDATE_RSVP, { itemId, status });
      return data.updateRSVP;
    },
    onSuccess: () => invalidate(),
    onError: () => Toast.show({ type: "error", text1: "ERROR_RSVP_FAILED" }),
  });
};

export const useLeaveEventMutation = (
  eventId: string,
  capacity: number | null,
) => {
  const invalidate = useInvalidateRSVPs();
  const { mutateAsync: deleteRSVP } = useDeleteRSVPMutation();
  const { mutateAsync: promoteFromWaitlist } = usePromoteFromWaitlistMutation();
  const { data: attendeeCount = 0 } = useGetAttendeeCountQuery(eventId);
  const { data: firstWaitlisted } = useGetWaitlistedRSVPQuery(eventId, true);

  return useMutation({
    mutationFn: async ({ itemId }: { itemId: string }) => {
      await deleteRSVP({ itemId });
      const wasFull = !!capacity && attendeeCount >= capacity;
      if (wasFull && firstWaitlisted) {
        await promoteFromWaitlist({ itemId: firstWaitlisted.ItemId });
      }
    },
    onSuccess: () => invalidate(),
    onError: () => Toast.show({ type: "error", text1: "ERROR_RSVP_FAILED" }),
  });
};

// Event Gallery
const useInvalidateEventGallery = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: [EVENT_GALLERY_QUERY_KEY] });
};

export const useGetEventGalleryQuery = (eventId: string) => {
  return useQuery({
    queryKey: [EVENT_GALLERY_QUERY_KEY, eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request<GetEventGalleryResponse>(
        GET_EVENT_GALLERY,
        {
          where: { eventId: { eq: eventId } },
          order: [{ field: "CreatedDate", direction: "DESC" }],
          pageNo: 1,
          pageSize: 50,
        },
      );
      return data.geteventgallerys.items;
    },
  });
};

export const useInsertEventGalleryMutation = () => {
  const invalidate = useInvalidateEventGallery();
  return useMutation({
    mutationFn: async ({
      eventId,
      imageUrl,
    }: {
      eventId: string;
      imageUrl: string;
    }) => {
      const client = await gqlClient();
      const data = await client.request<InsertEventGalleryResponse>(
        INSERT_EVENT_GALLERY,
        { eventId, imageUrl },
      );
      return data.inserteventgallery;
    },
    onSuccess: () => {
      invalidate();
      Toast.show({ type: "success", text1: "SUCCESS_PHOTO_UPLOAD" });
    },
    onError: () => {
      Toast.show({ type: "error", text1: "ERROR_PHOTO_UPLOAD" });
    },
  });
};

export const useDeleteEventGalleryMutation = () => {
  const invalidate = useInvalidateEventGallery();
  return useMutation({
    mutationFn: async ({ itemId }: { itemId: string }) => {
      const client = await gqlClient();
      const data = await client.request<DeleteEventGalleryResponse>(
        DELETE_EVENT_GALLERY,
        { itemId },
      );
      return data.deleteeventgallery;
    },
    onSuccess: () => {
      invalidate();
      Toast.show({ type: "error", text1: "SUCCESS_PHOTO_DELETE" });
    },
    onError: () => Toast.show({ type: "error", text1: "ERROR_PHOTO_DELETE" }),
  });
};
